import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { fetchKpiDashboardCharts } from './kpiDashboardApi';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  kpiResilienceCrossQueryKey,
  type KpiResilienceCrossSlice,
} from './kpiResilienceCrossFilter';

const STALE_MS = 60_000;
const GC_MS = 10 * 60_000;

/** Stable JSON for React Query key — mirrors `filterKey` in useKpiDashboardFilters but avoids any ordering bugs. */
function stableKpiChartsFilterSnapshot(filterValues: Record<string, string | string[]>): string {
  const ticketType = filterValues.ticketType;
  const state = filterValues.state;
  const assignmentGroup = filterValues.assignmentGroup;
  return JSON.stringify({
    duration: filterValues.duration,
    ticketType: Array.isArray(ticketType) ? [...ticketType].sort().join(',') : String(ticketType ?? ''),
    state: Array.isArray(state) ? [...state].sort().join(',') : String(state ?? ''),
    priority: filterValues.priority,
    customer: filterValues.customer,
    assignmentGroup: Array.isArray(assignmentGroup)
      ? [...assignmentGroup].sort().join(',')
      : String(assignmentGroup ?? ''),
    assignedToMe: filterValues.assignedToMe,
    assignedTo: filterValues.assignedTo,
  });
}

/**
 * Loads aggregated KPI dashboard data in one request.
 * - Query key includes filter snapshot for correct cache invalidation.
 * - Keeps previous chart data visible while refetching (smoother UX).
 * - Duration uses dateFrom/dateTo (presets + custom) like Service Request list.
 */
export function useKpiDashboardCharts(
  filterKey: string,
  filterValues: Record<string, string | string[]>,
  options: { enabled: boolean; resilienceCrossFilter?: KpiResilienceCrossSlice | null }
) {
  const { preferences } = useDateFormatter();
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const kpiTz = useMemo(
    () => ({ offsetMinutes: timezoneOffset, timeZone: preferences.timezone }),
    [timezoneOffset, preferences.timezone]
  );
  const crossKey = kpiResilienceCrossQueryKey(options.resilienceCrossFilter ?? null);
  const filterSnapshot = useMemo(() => stableKpiChartsFilterSnapshot(filterValues), [filterValues]);

  return useQuery({
    queryKey: ['kpi-dashboard-charts', filterKey, filterSnapshot, crossKey, preferences.timezone],
    queryFn: () =>
      fetchKpiDashboardCharts(filterValues, kpiTz, options.resilienceCrossFilter ?? null),
    enabled: options.enabled,
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: keepPreviousData,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
